/**
 * IMGLIB-1 · ② 上传图片弹窗（图片库和产品里共用同一个组件）。
 * 契约：designs/mockups/image-library/图片库-样稿.html 第 2 页。
 * · 拖进来或点选，可一次多张；地点 * / 景点或固定类别 * / 类型 *；名字 = 地点-景点-序号，系统自动起，不让手改。
 * · 前端先按和后端同一口径预判（宽高 / 大小 / 格式），不合格的几张拦下并写原因，其余照传；最终以后端为准（返回里每张写明成功或原因）。
 * · 从产品里打开（传 lock）：类型锁定为对应那一块，传完自动挂进这条线。
 * · IMGUP-1 · 一张一张发（并发 ≤ 2）：每张单独显示 排队中 / 上传中 / 已上传 / 失败（原因 + 重试）；一张失败不影响其它；
 *   不再把一批塞进同一个请求（总量超过 nginx 12 MB 上限会整批 413）。
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Upload, X } from "lucide-react";
import {
  KIND_LABEL, UPLOAD_CONCURRENCY, nextName, precheck, runPool, uploadFailReason, uploadOneToLibrary,
  type MediaKind, type UploadResponse, type UploadResult,
} from "@/services/mediaLibrary";
import { SubjectPicker, subjectReady, subjectToIds, type Subject } from "./SubjectPicker";

export interface UploadLock {
  kind: MediaKind;
  productId: string;
  /** 弹窗里显示的「传完会放进：…」 */
  where: string;
}

/** idle 还没点上传 · queued 排队中 · uploading 上传中 · done 已上传 · failed 失败（reason 写原因，可重试） */
type Status = "idle" | "queued" | "uploading" | "done" | "failed";
interface Item { id: number; file: File; dims: { w: number; h: number } | null; status: Status; reason?: string; mediaName?: string }
let _seq = 0;

/** 读图片宽高做前端预判；读不出（浏览器解码不了 / 超时）= null，交给后端判，不拦。 */
export function readDims(file: File, timeoutMs = 4000): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      const done = (v: { w: number; h: number } | null) => { window.clearTimeout(timer); URL.revokeObjectURL(url); resolve(v); };
      const timer = window.setTimeout(() => done(null), timeoutMs);
      img.onload = () => done({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => done(null);
      img.src = url;
    } catch { resolve(null); }
  });
}

const mb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function MediaUploadModal({ open, onClose, onUploaded, lock, defaultSubject }: {
  open: boolean;
  onClose: () => void;
  onUploaded: (r: UploadResponse) => void;
  lock?: UploadLock;
  defaultSubject?: Subject;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [subject, setSubject] = useState<Subject>(defaultSubject ?? { placeId: "", subject: "" });
  const [kind, setKind] = useState<MediaKind>(lock?.kind ?? "gallery");
  const [running, setRunning] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  /** 弹窗关掉 / 重开后，旧批次的请求回来不要再改状态 */
  const epoch = useRef(0);
  /** 各张当前状态（同步更新，批次结束时判断「还有没有失败的」用——state 要等渲染才变，这里等不及） */
  const statuses = useRef(new Map<number, Status>());

  useEffect(() => {
    if (open) { epoch.current++; statuses.current.clear(); setItems([]); setRunning(false); setKind(lock?.kind ?? "gallery"); setSubject(defaultSubject ?? { placeId: "", subject: "" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ids = subjectToIds(subject);
  const ready = subjectReady(subject);
  const nameQ = useQuery({
    queryKey: ["media-library", "next-seq", ids.place_id, ids.attraction_id, ids.category],
    enabled: open && ready,
    queryFn: () => nextName(ids),
  });

  // 前端预判只管还没发出去的那几张（idle）；已经发过的看服务端结果
  const check = (it: Item) => (it.status === "idle" ? precheck(kind, { name: it.file.name, size: it.file.size }, it.dims) : null);
  const idleOk = items.filter((it) => it.status === "idle" && !check(it));
  const names = nameQ.data ? idleOk.map((_, i) => `${nameQ.data!.prefix}-${String(nameQ.data!.next_seq + i).padStart(2, "0")}`) : [];
  const failedCount = items.filter((it) => it.status === "failed").length;
  const doneCount = items.filter((it) => it.status === "done").length;
  const touched = doneCount + failedCount > 0;
  const rejectedByPrecheck = items.filter((it) => it.status === "idle" && check(it)).length;

  const patch = (id: number, p: Partial<Item>) => {
    if (p.status) statuses.current.set(id, p.status);
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...p } : it)));
  };

  /** 一张一张发：并发 ≤ 2；每张的结果只改它自己那一行；全部结束后汇总（有成功的通知外面刷新；全成功关弹窗）。 */
  async function sendBatch(batch: Item[]) {
    if (!batch.length) return;
    const my = epoch.current;
    setRunning(true);
    batch.forEach((it) => patch(it.id, { status: "queued", reason: undefined }));
    const outcomes: UploadResult[] = [];
    await runPool(batch, UPLOAD_CONCURRENCY, async (it) => {
      if (epoch.current !== my) return;
      patch(it.id, { status: "uploading" });
      try {
        const r = await uploadOneToLibrary(it.file, { kind, ...ids, product_id: lock?.productId });
        const one = r.results[0];
        if (epoch.current !== my) return;
        if (one?.ok) { outcomes.push(one); patch(it.id, { status: "done", mediaName: one.name }); }
        else { patch(it.id, { status: "failed", reason: one?.reason || "上传失败，点重试" }); }
      } catch (e) {
        if (epoch.current !== my) return;
        patch(it.id, { status: "failed", reason: uploadFailReason(e) });
      }
    });
    if (epoch.current !== my) return;
    setRunning(false);
    if (outcomes.length > 0) {
      onUploaded({ results: outcomes, uploaded: outcomes.length, rejected: 0 } as UploadResponse);
      void nameQ.refetch();   // 序号往后顺延了，名字预览跟着更新
    }
    // 所有上传过的都成功了（包括重试补上的）→ 关弹窗（和以前一样）；还有失败的留着，让他重试
    if (![...statuses.current.values()].includes("failed")) onClose();
  }

  async function addFiles(list: FileList | File[] | null) {
    const arr = Array.from(list ?? []);
    if (!arr.length) return;
    const withDims = await Promise.all(arr.map(async (file) => ({ id: ++_seq, file, dims: await readDims(file), status: "idle" as Status })));
    setItems((cur) => [...cur, ...withDims]);
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label="上传图片"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4" data-testid="media-upload-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">上传图片</h3>
          <button type="button" aria-label="关闭" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_1fr]">
          <div className="flex flex-col gap-3">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); void addFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600"
              data-testid="media-dropzone"
            >
              <Upload size={18} />
              <div>把图片拖到这里，或 <b className="text-blue-600">点击选择</b></div>
              <div className="text-[11px] text-slate-400">可以一次选多张 · 这一批共用右边的归类，传完可以单张改</div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="media-file-input"
                onChange={(e) => { void addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            <div className="flex flex-col gap-1.5" data-testid="media-file-list">
              {items.map((it) => {
                const reason = check(it);
                const busy = it.status === "queued" || it.status === "uploading";
                return (
                  <div key={it.id} data-testid="media-file-row" data-status={it.status} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px]">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{it.file.name}
                        <span className="ml-1.5 text-[11px] text-slate-400">{it.dims ? `${it.dims.w}×${it.dims.h} · ` : ""}{mb(it.file.size)}</span></span>
                      {/* 失败原因放在文件名下面一行：原因可以很长，不把文件名挤没 */}
                      {it.status === "failed" && <span className="mt-0.5 block text-[11.5px] font-medium text-rose-600" data-testid="media-fail-reason">✕ {it.reason}</span>}
                    </span>
                    {it.status === "queued" && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-medium text-slate-500">排队中</span>}
                    {it.status === "uploading" && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11.5px] font-medium text-blue-600"><Loader2 size={11} className="animate-spin" />上传中…</span>}
                    {it.status === "done" && <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-medium text-emerald-600" title={it.mediaName ? `系统起的名字：${it.mediaName}` : undefined}>✓ 已上传</span>}
                    {it.status === "failed" && (
                      <>
                        <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600">失败</span>
                        <button type="button" data-testid="media-retry" disabled={running} onClick={() => void sendBatch([it])}
                          className="shrink-0 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11.5px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50">重试</button>
                      </>
                    )}
                    {it.status === "idle" && (reason
                      ? <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600">✕ {reason}</span>
                      : <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-medium text-emerald-600">✓ 合格</span>)}
                    <button type="button" aria-label="移除这张" disabled={busy} onClick={() => { statuses.current.delete(it.id); setItems((cur) => cur.filter((x) => x.id !== it.id)); }} className="shrink-0 text-slate-300 hover:text-rose-500 disabled:opacity-30"><X size={13} /></button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <SubjectPicker value={subject} onChange={setSubject} />
            <div className="flex flex-col gap-1 text-xs font-medium text-slate-600">类型 *
              <div className="inline-flex self-start overflow-hidden rounded-md border border-slate-300" role="radiogroup" aria-label="类型">
                {(["gallery", "highlight"] as MediaKind[]).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={kind === k} disabled={!!lock && lock.kind !== k} onClick={() => setKind(k)}
                    className={`px-3 py-1.5 text-[12.5px] ${kind === k ? "bg-blue-600 font-semibold text-white" : "bg-white text-slate-600 hover:bg-slate-50"} ${lock && lock.kind !== k ? "cursor-not-allowed opacity-40" : ""}`}>{KIND_LABEL[k]}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1 text-xs font-medium text-slate-600">名字（系统自动起）
              <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm text-slate-600" data-testid="media-name-preview">
                {names.length ? names.join("、") : ready ? "（没有合格的图时不显示）" : "先选地点和景点"}
              </div>
              <span className="text-[11px] font-normal text-slate-400">= 地点-景点-序号；序号接着这个景点已有的往下排，不让手改；文件原名只留作备查</span>
            </div>
            {lock && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                从产品里打开：传完自动放进 {lock.where}，类型跟着锁定。
              </div>
            )}
          </div>
        </div>
        {touched && !running && (
          <p className="mt-3 text-sm text-slate-600" role="status" data-testid="media-upload-summary">
            成功 {doneCount} 张，失败 {failedCount} 张{failedCount > 0 ? "（原因见左边，可以单张点「重试」）" : ""}。
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">取消</button>
          <button type="button" data-testid="media-upload-go" disabled={!ready || idleOk.length === 0 || running} onClick={() => void sendBatch(idleOk)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {running ? "上传中…" : `上传 ${idleOk.length} 张${rejectedByPrecheck > 0 ? `（不合格的 ${rejectedByPrecheck} 张不传）` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
